<style scoped>
    div.overlay {
        width: var(--width);
        height: var(--height);
        position: relative;
        overflow: hidden;
    }
    div.overlay:after {
        width: var(--width);
        height: var(--height);
        content: '';
        border-radius: 50%;
        background: transparent;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        transition: all 0.3s linear;
        pointer-events: none;
        opacity: 0.5;
        box-shadow: 0px 0px 0px var(--shadow) black;
    }

    .sk-fading-circle {
        right: 7px !important;
    }

    .croppa-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        height: 70px;
        width: 70px;
        margin-left: -35px;
        margin-top: -35px;
        -webkit-animation: spin 1s linear infinite;
                animation: spin 1s linear infinite;
        border: 3px solid #ddd;
        border-top: 3px solid #42a5f5;
        border-radius: 50%;
        }

        @-webkit-keyframes spin {
        to {
            border-top-color: #ec407a;
            -webkit-transform: rotate(360deg);
                    transform: rotate(360deg);
        }
        }

        @keyframes spin {
        to {
            border-top-color: #ec407a;
            -webkit-transform: rotate(360deg);
                    transform: rotate(360deg);
        }
        }
</style>

<template>
    <bc-dialog :value="uploadDialog" :max-width="width + 100" :mx-0="$bp.xs">
        <v-card>
            <v-card-title>
                <span class="headline">{{ $t('lang.uploadImage') }}</span>
                <v-spacer></v-spacer>
                <bc-btn-dialog-close
                    @invoke-method="invokeMethod($event), croppa.remove(), image_modified = false"
                    dialog-model="uploadDialog"
                    :dialog-status="uploadDialog"
                ></bc-btn-dialog-close>
            </v-card-title>
            <v-card-text class="text-center py-2">
                <div class="mx-auto" :class="removeShadow ? null : 'overlay'" :style="removeShadow ? {height: ($bp.xs ? height/2 : height) + 'px'} : {'--height': ($bp.xs ? height/2 : height) + 'px','--width': ($bp.xs ? width/2 : width) + 'px','--shadow': ($bp.xs ? width/2 : width) + 'px'}">
                    <croppa
                        ref="croppa"
                        v-model="croppa"
                        :width="$bp.xs ? width/2 : width"
                        :height="$bp.xs ? height/2 : height"
                        :accept="accept"
                        :prevent-white-space="true"
                        :show-remove-button="false"
                        :placeholder="placeholder_text"
                        :placeholder-font-size="placeholderFontSize"
                        :file-size-limit="fileSize"
                        :quality="$bp.xs ? 2 : quality"
                        :zoom-speed="zoomSpeed"
                        :show-loading="true"
                        :loading-size="40"
                        :loading-color="removeShadow ? '#606060' : '#ffffff'"
                        @move="modified()"
                        @zoom="modified()"
                        @loading-end="imageChanged()"
                        @file-type-mismatch="onFileTypeMismatch"
                        @file-size-exceed="onFileSizeExceed"
                        style="pointer-events:auto"
                    >
                        <div class="croppa-spinner" v-if="croppa && croppa.loading"></div>
                    </croppa>
                </div>
            </v-card-text>
            <div v-if="$bp.xs">
                <v-card-actions class="py-1">
                    <div class="mx-auto">
                        <v-btn :disabled="!croppa || !croppa.chosenFile" flat @mouseup.left="rotate(-1)">
                            <v-icon>rotate_left</v-icon>
                        </v-btn>
                        <v-btn :disabled="!croppa || !croppa.chosenFile" flat @mouseup.left="rotate(1)">
                            <v-icon>rotate_right</v-icon>
                        </v-btn>
                    </div>
                </v-card-actions>
                <v-card-actions class="py-1">
                    <div class="mx-auto">
                        <v-btn :disabled="!image_modified" color="secondary" @mouseup.left="applyMetadata()">{{ $t('lang.undo_changes') }}</v-btn>
                        <v-btn :disabled="!croppa || !croppa.chosenFile" color="error" @mouseup.left="replaceCroppa()">{{ $t('lang.replace') }}</v-btn>
                    </div>
                </v-card-actions>
            </div>
            <v-card-actions class="py-1" v-else>
                <div class="mx-auto">
                    <v-btn :disabled="!croppa || !croppa.chosenFile" flat @mouseup.left="rotate(-1)">
                        <v-icon>rotate_left</v-icon>
                    </v-btn>
                    <v-btn :disabled="!image_modified" color="secondary" @mouseup.left="applyMetadata()">{{ $t('lang.undo_changes') }}</v-btn>
                    <v-btn :disabled="!croppa || !croppa.chosenFile" color="error" @mouseup.left="replaceCroppa()">{{ $t('lang.replace') }}</v-btn>
                    <v-btn :disabled="!croppa || !croppa.chosenFile" flat @mouseup.left="rotate(1)">
                        <v-icon>rotate_right</v-icon>
                    </v-btn>
                </div>
            </v-card-actions>
            <v-card-actions>
                <v-btn block color="success" :disabled="!croppa || !croppa.chosenFile" @mouseup.left="uploadFunction(croppa.generateDataUrl('image/png'))">{{ $t('lang.save') }}</v-btn> <!-- uploadFunction(croppa.generateDataUrl('image/png')) -->
            </v-card-actions>
        </v-card>
    </bc-dialog>
</template>

<script>

import { mapGetters } from "vuex";

export default {
    computed: {
        placeholder_text(){

            if(this.placeholder === '') return this.$t('lang.choose_image');

            return this.placeholder;
        }
    },
    props: {
        uploadFunction: {
            type: Function
        },
        height: {
            type: Number,
            default: 600
        },
        width: {
            type: Number,
            default: 600
        },
        fileSize: {
            type: Number,
            default: 10 * 1000 * 1000 // 10 mb limit
        },
        placeholderFontSize: {
            type: Number,
            default: 16
        },
        placeholder: {
            type: String,
            default: ''
        },
        accept: {
            type: String,
            default: "image/png,image/jpeg,image/gif"
        },
        zoomSpeed: {
            type: Number,
            default: 10
        },
        quality: {
            type: Number,
            default: 1
        },
        removeShadow: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            croppa: null,
            uploadDialog: false,
            initialImage: null,
            image_modified: false,
            disable_rotate: false
        };
    },
    methods: {
        rotate(amt){
            if(!this.disabled_rotate){
                this.croppa.rotate(amt)

                this.disabled_rotate = true;
                setTimeout(() => {
                    this.disabled_rotate = false;
                }, 250)
            }
        },
        imageChanged(){
            let meta = this.croppa.getMetadata();
            meta.orientation == 6 ? this.croppa.rotate(-1) : null;
            this.initialImage = this.croppa.getMetadata();
        },
        onFileTypeMismatch (file) {
            alert('Invalid file type.')
        },
        onFileSizeExceed (file) {
            alert('File size exceeds.')
        },
        chooseFile(){
            this.uploadDialog = true;
            if(this.croppa) {
                this.croppa.chooseFile();
            }else{
                this.$nextTick(() => {
                    this.croppa.chooseFile();
                })
            }
        },
        applyMetadata(){
            // apply location data but manually handle orienation
            var metadata = {...this.initialImage};
            delete metadata['orientation'];

            let temp_meta = this.croppa.getMetadata();
            var currentOrientation = temp_meta.orientation;

            this.croppa.applyMetadata(metadata);
            // based on current orientation rotate back to original
            if(currentOrientation == '3'){ //base top
                this.croppa.rotate(2); //rotate 180
            }else if(currentOrientation == '6'){ //base left
                this.croppa.rotate(3); //rotate left
            }else if(currentOrientation == '8'){ //base right
                this.croppa.rotate(1); //rotate right
            }
            this.$nextTick(() => {
                this.image_modified = false;
            });
        },
        modified(){
            let temp_intial_img = {...this.initialImage};
            let current_img = this.croppa.getMetadata();

            temp_intial_img.scale = temp_intial_img.scale.toFixed(6);
            current_img.scale = current_img.scale.toFixed(6);

            this.image_modified = (JSON.stringify(temp_intial_img) === JSON.stringify(current_img) ? false : true);
        },
        replaceCroppa(){
            this.croppa.remove();
            this.croppa.chooseFile();
            setTimeout(() => { this.image_modified = false }, 10);
        }
    },
    watch: {
		'$bp.xs': function (newVal, oldVal){
            if(this.croppa){
                let canvas = this.croppa.getCanvas();
                if(newVal){
                    canvas.style.height = Math.round(this.height/2)+'px';
                    canvas.style.width = Math.round(this.width/2)+'px';
                }else{
                    canvas.style.height = this.height+'px';
                    canvas.style.width = this.width+'px';
                }
            }
        }
    }
};
</script>